// Catalog.ID Client — Public Claims Pane
// ============================================================================
// Lists the current identity's public claims (all statuses: pending, published, rejected).
// Allows deleting public claims.

import { createListPaneType } from '@catalog-shell';
import type { ListPaneConfig } from '@catalog-shell';
import { DataSource } from '@catalog-shell/components';
import type { ColumnDef, FetchResult } from '@catalog-shell/components';
import { claimTypeLabel } from './claims-list-pane';
import * as publicClaimsApi from '../api/public-claims';
import type { PublicClaimSummary } from '../api/types';
import { PANE_IDS } from '../shell-config';

type PublicClaimStatus = 'pending' | 'published' | 'rejected';

const STATUS_COLORS: Record<PublicClaimStatus, string> = {
    pending: 'rgba(100,149,237,0.3)',
    published: 'rgba(80,200,80,0.3)',
    rejected: 'rgba(220,50,50,0.3)',
};

/** Display names for `publicclaim.keyclass` (chat migration §3; mirrors the
 *  server's closed `PublicClaimKeyClasses` vocabulary). */
const KEY_CLASS_LABELS: Record<string, string> = {
    machine: 'Machine',
    chat_root: 'Chat root',
    mail_root: 'Mail root',
};

const STATUS_LABELS: Record<PublicClaimStatus, string> = {
    pending: 'Pending',
    published: 'Published',
    rejected: 'Rejected',
};

const columns: ColumnDef<PublicClaimSummary>[] = [
    {
        key: 'claimType',
        header: 'Type',
        width: 120,
        sortable: true,
        getValue: (item) => item.claimType,
        type: {
            kind: 'text',
            converter: (v) => claimTypeLabel(v as string),
        },
    },
    {
        key: 'publicValue',
        header: 'Value',
        width: 200,
        getValue: (item) => item.publicValue,
        type: {
            kind: 'text',
            converter: (v) => {
                const s = v as string;
                if (!s) return '—';
                return s.length > 60 ? s.slice(0, 60) + '…' : s;
            },
            textColor: (v) => v ? undefined : '#555',
        },
    },
    {
        key: 'status',
        header: 'Status',
        width: 100,
        getValue: (item) => item.status,
        type: {
            kind: 'badge',
            backgroundColor: (v) => STATUS_COLORS[v as PublicClaimStatus] ?? '#555',
            converter: (v) => STATUS_LABELS[v as PublicClaimStatus] ?? String(v),
        },
    },
    {
        // Key class on the `registry_signing_key` track (chat migration §3).
        // Without it a per-machine Registry key and an account chat root are
        // indistinguishable rows of the same type — the server's own List
        // handler says this column exists so the publication pane can tell them
        // apart. Blank for every other claim type, where the class is
        // meaningless.
        key: 'keyClass',
        header: 'Class',
        width: 90,
        getValue: (item) => item.keyClass ?? '',
        type: {
            kind: 'text',
            converter: (v) => {
                const s = v as string;
                if (!s) return '—';
                return KEY_CLASS_LABELS[s] ?? s;
            },
            textColor: (v) => v ? undefined : '#555',
        },
        responsiveHide: { minContainerWidth: 700, priority: 2 },
    },
    {
        key: 'publishedAt',
        header: 'Published',
        width: 100,
        sortable: true,
        getValue: (item) => item.publishedAt,
        type: {
            kind: 'text',
            converter: (v) => {
                if (!v) return '—';
                try { return new Date(v as string).toLocaleDateString(); }
                catch { return String(v); }
            },
            textColor: (v) => v ? undefined : '#555',
        },
        responsiveHide: { minContainerWidth: 600, priority: 1 },
    },
];

function createPublicClaimsDataSource(): DataSource<PublicClaimSummary> {
    return new DataSource<PublicClaimSummary>({
        fetch: async (params): Promise<FetchResult<PublicClaimSummary>> => {
            const claims = await publicClaimsApi.listPublicClaims();
            const sorted = [...claims];
            if (params.sortBy) {
                const key = params.sortBy;
                const dir = params.sortDirection === 'desc' ? -1 : 1;
                sorted.sort((a, b) => {
                    const va = (a as unknown as Record<string, unknown>)[key] ?? '';
                    const vb = (b as unknown as Record<string, unknown>)[key] ?? '';
                    return va < vb ? -dir : va > vb ? dir : 0;
                });
            }
            const sliced = sorted.slice(params.offset, params.offset + params.limit);
            return { items: sliced, totalCount: claims.length };
        },
        keyFn: (item) => String(item.publicClaimId),
        pageSize: 100,
    });
}

const config: ListPaneConfig<PublicClaimSummary> = {
    typeId: PANE_IDS.publicClaims,
    defaultTitle: 'Public Claims',
    createDataSource: () => createPublicClaimsDataSource(),
    columns,
    keyFn: (item) => String(item.publicClaimId),
    initialMode: 'table',
    enabledModes: ['table'],
    selectionMode: 'none',
    onCreated: ({ dataSource }, ctx) => {
        ctx.disposables.add(ctx.shell.on('data-changed', (data) => {
            if ((data as { type?: string })?.type === 'publicclaims') {
                dataSource.refresh();
            }
        }));
    },
    onItemSelect: (item, ctx) => {
        // Open a detail view in flow surface (for rejection reason, delete action)
        ctx.surface?.openPane({
            typeId: PANE_IDS.publicClaimDetail,
            title: `${claimTypeLabel(item.claimType)} (${STATUS_LABELS[item.status as PublicClaimStatus] ?? item.status})`,
            context: { publicClaim: item },
        }, { originPaneId: ctx.instance.instanceId });
    },
    ariaLabel: 'Public claims list',
};

export function createPublicClaimsPane() {
    return createListPaneType(config);
}
